import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { format, parseISO, addDays } from 'date-fns';
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Mail,
  Phone as PhoneIcon,
  Search,
  User,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { bookingService } from '@/services/api/booking.service';
import { adminBookingService } from '@/services/api/admin-booking.service';
import { organizationService } from '@/services/api/organization.service';
import { customerService } from '@/services/api/customer.service';
import type { Service, Provider } from '@/types/booking';
import type { Customer } from '@/types/customer';

import { Button } from '@/components/ui/button';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PhoneInput } from '@/components/ui/phone-input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SearchBar } from '@/components/ui/search-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, StepCard, type StepState } from '@/components/booking/Stepper';
import { BookingSummaryCard } from '@/components/booking/BookingSummaryCard';
import { BookingTypeStep, type BookingType } from '@/components/booking/BookingTypeStep';
import {
  RecurrencePreviewList,
  type RecurrenceOccurrence,
} from '@/components/booking/RecurrencePreviewList';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

function createCustomerSchema(requirePhone: boolean, t: TFunction) {
  return z.object({
    email: z.string().email(t('validation:email.invalid')),
    first_name: z.string().min(1, t('validation:firstName.required')),
    last_name: z.string().min(1, t('validation:lastName.required')),
    phone: requirePhone
      ? z
          .string()
          .min(1, t('validation:phone.required'))
          .refine(isValidPhoneNumber, t('validation:phone.invalid'))
      : z
          .string()
          .optional()
          .or(z.literal(''))
          .refine((v) => !v || isValidPhoneNumber(v), t('validation:phone.invalid')),
    notes: z.string().optional(),
    status: z.enum(['pending', 'confirmed']),
  });
}

type CustomerFormData = z.infer<ReturnType<typeof createCustomerSchema>>;
type WizardStep = 0 | 1 | 2 | 3 | 4 | 5;
type CustomerMode = 'existing' | 'new';
type EndMode = 'count' | 'date';

const SEARCH_DEBOUNCE_MS = 250;

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

const WEEKDAYS: Array<{ value: number; shortKey: string }> = [
  { value: 1, shortKey: 'mon' },
  { value: 2, shortKey: 'tue' },
  { value: 3, shortKey: 'wed' },
  { value: 4, shortKey: 'thu' },
  { value: 5, shortKey: 'fri' },
  { value: 6, shortKey: 'sat' },
  { value: 0, shortKey: 'sun' },
];

function isOneOnOneService(svc: Service): boolean {
  const cap = svc.max_capacity ?? svc.maxCapacity ?? 1;
  return cap <= 1;
}

function isGroupService(svc: Service): boolean {
  const cap = svc.max_capacity ?? svc.maxCapacity ?? 1;
  return cap > 1 || !!(svc.group_booking ?? svc.groupBooking);
}

export default function NewRecurringBookingPage() {
  const [, setLocation] = useLocation();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation('bookings');
  const queryClient = useQueryClient();

  useSetPageHeader(
    {
      title: t('recurring.title', 'New recurring booking'),
      sub: t('recurring.subtitle', 'Create a weekly series for a customer'),
    },
    [t],
  );

  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });
  const requirePhone = organization?.booking_settings?.require_phone ?? false;
  const schema = useMemo(() => createCustomerSchema(requirePhone, t), [requirePhone, t]);

  const presetCustomerId = useMemo(() => getQueryParam('customer_id'), []);
  const initialStep = useMemo<WizardStep>(() => {
    const raw = getQueryParam('step');
    const parsed = raw ? Number(raw) : NaN;
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 6) {
      return (parsed - 1) as WizardStep;
    }
    return 0;
  }, []);

  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [bookingType, setBookingType] = useState<BookingType>('recurring');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);

  // Customer
  const [customerMode, setCustomerMode] = useState<CustomerMode>('existing');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');

  // Preload customer when ?customer_id=… is present.
  const { data: presetCustomer } = useQuery({
    queryKey: ['customer', presetCustomerId],
    queryFn: () => customerService.getCustomer(presetCustomerId!),
    enabled: !!presetCustomerId,
  });

  useEffect(() => {
    if (presetCustomer) {
      setSelectedCustomer(presetCustomer);
      setCustomerMode('existing');
    }
  }, [presetCustomer]);

  // Recurrence config
  const [firstDate, setFirstDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  });
  const [time, setTime] = useState<string>('10:00');
  const [endMode, setEndMode] = useState<EndMode>('count');
  const [occurrences, setOccurrences] = useState<number>(8);
  // Local text state so the input can be cleared mid-edit; `occurrences` only
  // ever holds a clamped number.
  const [occurrencesText, setOccurrencesText] = useState('8');
  const [endDate, setEndDate] = useState<string>(format(addDays(new Date(), 56), 'yyyy-MM-dd'));

  // Preview state
  const [preview, setPreview] = useState<RecurrenceOccurrence[] | null>(null);
  const [previewCapped, setPreviewCapped] = useState(false);
  const [shiftingIndex, setShiftingIndex] = useState<number | null>(null);
  const [adhocSet, setAdhocSet] = useState<Set<string>>(new Set());

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      notes: '',
      status: 'confirmed',
    },
  });
  const formData = watch();

  const { data: services = [], isLoading: isLoadingServices } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });
  // Both 1:1 and group services can be recurring. Group services route to the
  // dedicated group occurrence-picker; 1:1 services continue in this wizard.
  const recurringServices = useMemo(
    () => services.filter((s) => s.active && (isOneOnOneService(s) || isGroupService(s))),
    [services],
  );

  function handleServiceSelect(svc: Service) {
    if (isGroupService(svc)) {
      const params = new URLSearchParams({ service_id: String(svc.id) });
      if (presetCustomerId) params.set('customer_id', presetCustomerId);
      setLocation(`/admin/bookings/new/group-recurring?${params.toString()}`);
      return;
    }
    setSelectedService(svc);
    setSelectedProvider(null);
  }

  const { data: providers = [] } = useQuery({
    queryKey: ['providers', selectedService?.id],
    queryFn: () => bookingService.getProviders(),
    enabled: !!selectedService,
  });
  const activeProviders = useMemo(() => providers.filter((p) => p.active), [providers]);

  // Customer search debounce
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCustomerSearch(customerSearch.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [customerSearch]);

  const { data: customerSearchResult, isFetching: isSearchingCustomers } = useQuery({
    queryKey: ['customer-search', debouncedCustomerSearch],
    queryFn: () =>
      customerService.list({
        search: debouncedCustomerSearch || undefined,
        page: 1,
        perPage: 8,
      }),
    enabled: customerMode === 'existing' && currentStep === 3,
  });

  // DST detection on the committed preview
  const dstWarning = useMemo(() => {
    if (!preview?.length) return false;
    const first = new Date(preview[0].datetime);
    const last = new Date(preview[preview.length - 1].datetime);
    return first.getTimezoneOffset() !== last.getTimezoneOffset();
  }, [preview]);

  function firstDateTimeISO(): string {
    const [hh, mm] = time.split(':').map(Number);
    const d = new Date(firstDate);
    d.setHours(hh, mm, 0, 0);
    return d.toISOString();
  }

  function customerEmailForPreview(): string | undefined {
    if (customerMode === 'existing') return selectedCustomer?.email || undefined;
    return formData.email || undefined;
  }

  function pickWeekday(targetDow: number) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let diff = ((targetDow - today.getDay()) + 7) % 7;
    if (diff === 0) diff = 7;
    setFirstDate(addDays(today, diff));
  }

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!selectedService || !selectedProvider)
        throw new Error(t('recurring.errors.serviceProviderRequired'));
      const recurrence =
        endMode === 'count'
          ? { occurrences }
          : { end_date: endDate };
      return adminBookingService.previewRecurringBooking({
        service_id: selectedService.id,
        provider_id: selectedProvider.id,
        first_start_time: firstDateTimeISO(),
        recurrence,
        customer_email: customerEmailForPreview(),
      });
    },
    onSuccess: (result) => {
      setPreview(result.occurrences);
      setPreviewCapped(!!result.capped);
      setAdhocSet(new Set());
      setCurrentStep(5);
    },
    onError: (err: any) => toast.error(err?.message || t('recurring.errors.previewFailed')),
  });

  const createMutation = useMutation({
    mutationFn: async (data: CustomerFormData) => {
      if (!selectedService || !selectedProvider)
        throw new Error(t('recurring.errors.serviceProviderRequired'));
      const recurrence =
        endMode === 'count'
          ? { occurrences }
          : { end_date: endDate };

      const customer =
        customerMode === 'existing' && selectedCustomer
          ? {
              customer_first_name: selectedCustomer.firstName,
              customer_last_name: selectedCustomer.lastName,
              customer_email: selectedCustomer.email,
              customer_phone: selectedCustomer.phone || undefined,
              customer_id: selectedCustomer.publicId ?? selectedCustomer.id,
            }
          : {
              customer_first_name: data.first_name,
              customer_last_name: data.last_name,
              customer_email: data.email,
              customer_phone: data.phone || undefined,
            };

      return adminBookingService.createRecurringBooking(
        {
          service_id: selectedService.id,
          provider_id: selectedProvider.id,
          first_start_time: firstDateTimeISO(),
          recurrence,
          ...customer,
          notes: data.notes || undefined,
          initial_status: data.status,
          force_create_availability_for: adhocSet.size ? Array.from(adhocSet) : undefined,
        },
        { force: true },
      );
    },
    onSuccess: (result) => {
      toast.success(
        t('recurring.toasts.seriesCreated', '{{count}} bookings created', { count: result.count }),
      );
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
      setLocation('/admin/bookings');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || t('recurring.errors.createFailed');
      toast.error(msg);
    },
  });

  async function shiftOccurrence(idx: number, dir: -1 | 1) {
    if (!preview || !selectedService || !selectedProvider) return;
    const row = preview[idx];
    const newDt = new Date(new Date(row.datetime).getTime() + dir * 7 * 86_400_000);
    setShiftingIndex(idx);
    try {
      const result = await adminBookingService.previewRecurringBooking({
        service_id: selectedService.id,
        provider_id: selectedProvider.id,
        first_start_time: newDt.toISOString(),
        recurrence: { occurrences: 1 },
        customer_email: customerEmailForPreview(),
      });
      const replacement = result.occurrences?.[0];
      if (replacement) {
        setPreview((cur) => {
          if (!cur) return cur;
          const next = [...cur];
          next[idx] = replacement as RecurrenceOccurrence;
          return next;
        });
      }
    } catch (e: any) {
      toast.error(e?.message || t('recurring.errors.shiftFailed'));
    } finally {
      setShiftingIndex(null);
    }
  }

  // Step navigation helpers
  const stepStateAt = (idx: number): StepState =>
    idx < currentStep ? 'done' : idx === currentStep ? 'active' : 'locked';
  const onEditAt = (idx: number) =>
    idx < currentStep ? () => setCurrentStep(idx as WizardStep) : undefined;

  const canStep0 = bookingType !== null;
  const canStep1 = selectedService !== null;
  const canStep2 = selectedProvider !== null;
  const canStep3 =
    customerMode === 'existing'
      ? selectedCustomer !== null
      : !!(formData.first_name && formData.last_name && formData.email);
  const canStep4 = true;

  const handleBookingTypeNext = () => {
    if (bookingType === 'one_time') {
      const params = new URLSearchParams({ step: '2' });
      if (presetCustomerId) params.set('customer_id', presetCustomerId);
      setLocation(`/admin/bookings/new?${params.toString()}`);
      return;
    }
    setCurrentStep(1);
  };

  const bookingTypeSummary = (() => {
    if (bookingType === 'one_time') return t('newBooking.bookingType.oneTime');
    if (bookingType === 'recurring') return t('newBooking.bookingType.recurring');
    return undefined;
  })();

  const serviceSummary = selectedService
    ? `${selectedService.name} · ${selectedService.duration || selectedService.duration_minutes} min`
    : undefined;
  const providerSummary = selectedProvider?.displayName;
  const customerSummary = (() => {
    if (customerMode === 'existing' && selectedCustomer) {
      return `${selectedCustomer.fullName} · ${selectedCustomer.email}`;
    }
    const name = [formData.first_name, formData.last_name].filter(Boolean).join(' ');
    const parts = [name, formData.email].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  })();
  const recurrenceSummary = (() => {
    const dayLabel = format(firstDate, 'EEE d MMM');
    const endLabel =
      endMode === 'count'
        ? t('recurring.summary.afterN', '{{n}} times', { n: occurrences })
        : t('recurring.summary.untilDate', 'until {{date}}', { date: endDate });
    return `${dayLabel} · ${time} · ${endLabel}`;
  })();

  const okCount = preview ? preview.filter((o) => o.ok).length : 0;
  const totalToCreate = okCount + adhocSet.size;

  const adhocEligibleDts = useMemo(
    () =>
      preview
        ? preview
            .filter(
              (o) =>
                !o.ok &&
                o.reason === 'no_availability' &&
                o.details?.can_create_availability,
            )
            .map((o) => o.datetime)
        : [],
    [preview],
  );
  const allAdhocSelected =
    adhocEligibleDts.length > 0 && adhocEligibleDts.every((d) => adhocSet.has(d));

  function toggleAllAdhoc() {
    setAdhocSet((prev) => {
      const next = new Set(prev);
      if (allAdhocSelected) {
        adhocEligibleDts.forEach((d) => next.delete(d));
      } else {
        adhocEligibleDts.forEach((d) => next.add(d));
      }
      return next;
    });
  }

  const seriesCount = (() => {
    if (preview) return totalToCreate || preview.length;
    if (endMode === 'count') return occurrences;
    return undefined;
  })();
  const totalValue =
    selectedService && !selectedService.hide_price && seriesCount
      ? formatCurrency((selectedService.price || 0) * seriesCount)
      : undefined;
  const dashSoft = <span className="text-soft">—</span>;
  const summaryRows = [
    {
      label: t('recurring.summary.service', 'Service'),
      value: serviceSummary ?? dashSoft,
    },
    {
      label: t('recurring.summary.provider', 'Team member'),
      value: providerSummary ?? dashSoft,
    },
    {
      label: t('recurring.summary.customer', 'Customer'),
      value: customerSummary ?? dashSoft,
    },
    {
      label: t('recurring.summary.when', 'When'),
      value: currentStep >= 4 ? recurrenceSummary : dashSoft,
    },
    {
      label: t('recurring.summary.occurrences', 'Occurrences'),
      value: preview
        ? totalToCreate || preview.length
        : seriesCount ?? dashSoft,
    },
  ];

  return (
    <div className="space-y-6" data-testid="recurring-booking-wizard">

      <div className="mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
        <Stepper active={currentStep + 1}>
        {/* Step 1 — Booking Type */}
        <StepCard
          step={1}
          title={t('newBooking.stepTitles.step1')}
          state={stepStateAt(0)}
          summary={bookingTypeSummary}
          onEdit={onEditAt(0)}
        >
          <BookingTypeStep value={bookingType} onChange={setBookingType} t={t} />
          <StepNav
            onPrev={undefined}
            onNext={handleBookingTypeNext}
            disabledNext={!canStep0}
            t={t}
          />
        </StepCard>

        {/* Step 2 — Service */}
        <StepCard
          step={2}
          title={t('recurring.steps.service', 'Pick a service')}
          state={stepStateAt(1)}
          summary={serviceSummary}
          onEdit={onEditAt(1)}
        >
          {isLoadingServices ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
            </div>
          ) : recurringServices.length === 0 ? (
            <Alert>
              <AlertDescription>
                {t('recurring.noServices', 'No active services available.')}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid gap-2" role="radiogroup">
              {recurringServices.map((s) => {
                const selected = selectedService?.id === s.id;
                const duration = s.duration || s.duration_minutes;
                const group = isGroupService(s);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleServiceSelect(s)}
                    aria-checked={selected}
                    role="radio"
                    className={cn(
                      'text-left rounded-2xl border p-4 transition-colors',
                      selected
                        ? 'border-brand-500 bg-brand-50 shadow-sm'
                        : 'border-line hover:border-brand-300 bg-surface',
                    )}
                    data-testid="recurring-service-card"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-semibold text-ink">
                          {s.name}
                          {group && (
                            <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 border border-violet-200">
                              <Users className="h-3 w-3 mr-1" />
                              {t('recurring.groupBadge', 'Group')}
                            </Badge>
                          )}
                        </p>
                        {s.description && (
                          <p className="text-body-sm text-soft mt-0.5 line-clamp-2">{s.description}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-body-sm text-soft">{duration} min</p>
                        {!s.hide_price && (
                          <p className="text-body-sm font-semibold text-ink">
                            {formatCurrency(s.price || 0)}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <StepNav
            onPrev={() => setCurrentStep(0)}
            onNext={() => setCurrentStep(2)}
            disabledNext={!canStep1}
            t={t}
          />
        </StepCard>

        {/* Step 3 — Provider */}
        <StepCard
          step={3}
          title={t('recurring.steps.provider', 'Pick team member')}
          state={stepStateAt(2)}
          summary={providerSummary}
          onEdit={onEditAt(2)}
        >
          {activeProviders.length === 0 ? (
            <Alert>
              <AlertDescription>
                {t('recurring.noProviders', 'No team members available for this service.')}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid gap-2" role="radiogroup">
              {activeProviders.map((p) => {
                const selected = selectedProvider?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedProvider(p)}
                    aria-checked={selected}
                    role="radio"
                    className={cn(
                      'flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors',
                      selected
                        ? 'border-brand-500 bg-brand-50 shadow-sm'
                        : 'border-line hover:border-brand-300 bg-surface',
                    )}
                    data-testid="recurring-provider-card"
                  >
                    <div className="h-10 w-10 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-semibold">
                      {p.displayName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">{p.displayName}</p>
                      {(p as any).title && (
                        <p className="text-body-sm text-soft">{(p as any).title}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <StepNav
            onPrev={() => setCurrentStep(1)}
            onNext={() => setCurrentStep(3)}
            disabledNext={!canStep2}
            t={t}
          />
        </StepCard>

        {/* Step 4 — Customer */}
        <StepCard
          step={4}
          title={t('recurring.steps.customer', 'Customer')}
          state={stepStateAt(3)}
          summary={customerSummary}
          onEdit={onEditAt(3)}
        >
          <Tabs value={customerMode} onValueChange={(v) => setCustomerMode(v as CustomerMode)}>
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="existing" data-testid="tab-existing-customer">
                <Search className="h-4 w-4 mr-2" />
                {t('newBooking.customer.existing', 'Existing customer')}
              </TabsTrigger>
              <TabsTrigger value="new" data-testid="tab-new-customer">
                <User className="h-4 w-4 mr-2" />
                {t('newBooking.customer.new', 'New customer')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="existing" className="space-y-4 mt-4">
              {selectedCustomer ? (
                <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-brand-500 text-white flex items-center justify-center font-semibold">
                    {selectedCustomer.fullName.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-ink">{selectedCustomer.fullName}</p>
                    <p className="text-body-sm text-soft">
                      {selectedCustomer.email}
                      {selectedCustomer.phone ? ` · ${selectedCustomer.phone}` : ''}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)}>
                    {t('newBooking.customer.clear', 'Change')}
                  </Button>
                </div>
              ) : (
                <>
                  <SearchBar
                    placeholder={t(
                      'newBooking.customer.searchPlaceholder',
                      'Search by name or email…',
                    )}
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    data-testid="customer-search-input"
                  />
                  {isSearchingCustomers && (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-soft" />
                    </div>
                  )}
                  {!isSearchingCustomers &&
                    customerSearch.trim().length > 0 &&
                    (customerSearchResult?.customers?.length ?? 0) === 0 && (
                      <Alert>
                        <AlertDescription>
                          {t('newBooking.customer.noResults', 'No customers match your search.')}
                        </AlertDescription>
                      </Alert>
                    )}
                  {!isSearchingCustomers &&
                    (customerSearchResult?.customers?.length ?? 0) > 0 &&
                    (() => {
                      const all = customerSearchResult!.customers;
                      const isSearching = debouncedCustomerSearch.length > 0;
                      const shown = isSearching ? all : all.slice(0, 3);
                      return (
                        <>
                          {!isSearching && (
                            <p className="text-body-xs text-soft">
                              {t(
                                'newBooking.customer.recentHint',
                                'Showing recent customers — type to search.',
                              )}
                            </p>
                          )}
                          <div className="rounded-xl border border-line divide-y divide-line">
                            {shown.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => setSelectedCustomer(c)}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-2 transition-colors"
                                data-testid="customer-result"
                              >
                                <div className="h-9 w-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-semibold">
                                  {c.fullName.slice(0, 1).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-ink truncate">{c.fullName}</p>
                                  <p className="text-body-sm text-soft truncate">
                                    {c.email}
                                    {c.phone ? ` · ${c.phone}` : ''}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                </>
              )}
            </TabsContent>

            <TabsContent value="new" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label={t('newBooking.form.firstName', 'First name')}
                  required
                  icon={<User className="h-4 w-4" />}
                  error={errors.first_name?.message}
                >
                  <Controller
                    name="first_name"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} className={cn(errors.first_name && 'border-red-500')} />
                    )}
                  />
                </FormField>
                <FormField
                  label={t('newBooking.form.lastName', 'Last name')}
                  required
                  icon={<User className="h-4 w-4" />}
                  error={errors.last_name?.message}
                >
                  <Controller
                    name="last_name"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} className={cn(errors.last_name && 'border-red-500')} />
                    )}
                  />
                </FormField>
                <FormField
                  label={t('newBooking.form.email', 'Email')}
                  required
                  icon={<Mail className="h-4 w-4" />}
                  error={errors.email?.message}
                >
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder={t('newBooking.form.emailPlaceholder', 'customer@example.com')}
                        className={cn(errors.email && 'border-red-500')}
                      />
                    )}
                  />
                </FormField>
                <FormField
                  label={
                    requirePhone
                      ? t('newBooking.form.phone', 'Phone')
                      : t('newBooking.form.phoneOptional', 'Phone (optional)')
                  }
                  required={requirePhone}
                  icon={<PhoneIcon className="h-4 w-4" />}
                  error={errors.phone?.message}
                >
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput value={field.value || ''} onChange={field.onChange} />
                    )}
                  />
                </FormField>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-4 space-y-4">
            <FormField
              label={t('newBooking.form.initialStatus', 'Initial status')}
              icon={<FileText className="h-4 w-4" />}
            >
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">
                        {t('newBooking.form.statusConfirmed', 'Confirmed')}
                      </SelectItem>
                      <SelectItem value="pending">
                        {t('newBooking.form.statusPending', 'Pending')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
            <FormField
              label={t('newBooking.form.notesOptional', 'Notes (optional)')}
              icon={<FileText className="h-4 w-4" />}
            >
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={3}
                    placeholder={t('newBooking.form.notesPlaceholder', 'Add notes…')}
                  />
                )}
              />
            </FormField>
          </div>

          <StepNav
            onPrev={() => setCurrentStep(2)}
            onNext={() => {
              if (customerMode === 'new') {
                handleSubmit(() => setCurrentStep(4))();
              } else {
                setCurrentStep(4);
              }
            }}
            disabledNext={!canStep3}
            t={t}
          />
        </StepCard>

        {/* Step 5 — Recurrence */}
        <StepCard
          step={5}
          title={t('recurring.steps.recurrence', 'Recurrence')}
          state={stepStateAt(4)}
          summary={currentStep > 4 ? recurrenceSummary : undefined}
          onEdit={onEditAt(4)}
        >
          <div className="space-y-6">
            <div>
              <Label>{t('recurring.weekday', 'Weekday')}</Label>
              <div
                className="mt-2 grid grid-cols-7 gap-1.5"
                role="radiogroup"
                aria-label={t('recurring.weekday', 'Weekday')}
              >
                {WEEKDAYS.map((d) => {
                  const selected = firstDate.getDay() === d.value;
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => pickWeekday(d.value)}
                      role="radio"
                      aria-checked={selected}
                      className={cn(
                        'h-10 rounded-lg border text-body-sm font-semibold uppercase tracking-wide transition-colors',
                        selected
                          ? 'bg-brand-500 border-brand-500 text-white shadow-sm'
                          : 'bg-surface border-line text-ink hover:border-brand-300 hover:bg-brand-50',
                      )}
                      data-testid={`recurring-weekday-${d.shortKey}`}
                    >
                      {t(`timeSlots:days.${d.shortKey}`)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label={t('recurring.firstDate', 'First date')}>
                <Input
                  type="date"
                  value={format(firstDate, 'yyyy-MM-dd')}
                  onChange={(e) => setFirstDate(parseISO(e.target.value))}
                />
                <p className="mt-1 text-body-xs text-soft">
                  {t('recurring.firstDateHint', 'Series repeats every 7 days from this date')}
                </p>
              </FormField>
              <FormField label={t('recurring.time', 'Time')}>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </FormField>
            </div>

            <FormField label={t('recurring.endCondition.label', 'Ends')}>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr,180px] gap-3">
                <Select value={endMode} onValueChange={(v) => setEndMode(v as EndMode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="count">
                      {t('recurring.endCondition.afterOccurrences', 'After N occurrences')}
                    </SelectItem>
                    <SelectItem value="date">
                      {t('recurring.endCondition.onDate', 'On a specific date')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {endMode === 'count' && (
                  <Input
                    type="number"
                    min={1}
                    max={52}
                    value={occurrencesText}
                    onChange={(e) => {
                      setOccurrencesText(e.target.value);
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n)) setOccurrences(Math.max(1, Math.min(52, n)));
                    }}
                    onBlur={() => {
                      const n = parseInt(occurrencesText, 10);
                      const clamped = Number.isNaN(n) ? 1 : Math.max(1, Math.min(52, n));
                      setOccurrencesText(String(clamped));
                      setOccurrences(clamped);
                    }}
                  />
                )}
                {endMode === 'date' && (
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                )}
              </div>
              <p className="mt-2 text-body-xs text-soft">
                {t(
                  'recurring.endCondition.cap',
                  'Maximum 52 occurrences per series.',
                )}
              </p>
            </FormField>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(3)} data-testid="back-button">
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('newBooking.buttons.back', 'Back')}
            </Button>
            <Button
              onClick={() => previewMutation.mutate()}
              disabled={previewMutation.isPending || !canStep4}
              data-testid="preview-series-button"
            >
              {previewMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('recurring.actions.previewLoading', 'Building preview…')}
                </>
              ) : (
                <>
                  {t('recurring.actions.preview', 'Preview series')}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </StepCard>

        {/* Step 6 — Preview & Create */}
        <StepCard
          step={6}
          title={t('recurring.steps.preview', 'Preview & confirm')}
          state={stepStateAt(5)}
        >
          {preview && (
            <div className="space-y-4" data-testid="recurring-preview">
              <Alert>
                <Calendar className="h-4 w-4" />
                <AlertTitle>{t('recurring.preview.title', 'Series preview')}</AlertTitle>
                <AlertDescription>
                  {t(
                    'recurring.preview.summary',
                    '{{ok}} of {{total}} occurrences available. Unavailable ones will be skipped.',
                    { ok: okCount, total: preview.length },
                  )}
                </AlertDescription>
              </Alert>

              {previewCapped && (
                <Alert className="border-amber-200 bg-amber-50">
                  <Calendar className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-900">
                    {t('recurring.preview.cappedTitle', 'Series capped at 52 occurrences')}
                  </AlertTitle>
                  <AlertDescription className="text-amber-800">
                    {t(
                      'recurring.preview.cappedDescription',
                      'You asked for more than the system limit. Create a follow-up series after the last occurrence to extend further.',
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {dstWarning && (
                <Alert className="border-amber-200 bg-amber-50">
                  <Calendar className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-900">
                    {t('recurring.dst.title', 'Daylight Saving change inside series')}
                  </AlertTitle>
                  <AlertDescription className="text-amber-800">
                    {t(
                      'recurring.dst.description',
                      'This series crosses a DST boundary. Wall-clock time is preserved per occurrence; double-check that the local time still works for the customer.',
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {adhocEligibleDts.length > 0 && (
                <div className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-body-sm font-semibold text-amber-900">
                      {t(
                        'recurring.preview.adhocSelectAllTitle',
                        '{{count}} slots without availability',
                        { count: adhocEligibleDts.length },
                      )}
                    </p>
                    <p className="text-body-xs text-amber-800">
                      {t(
                        'recurring.preview.adhocSelectAllHint',
                        'Bulk-create ad-hoc availability for these dates.',
                      )}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="rounded border-line text-amber-600 focus:ring-amber-500"
                      checked={allAdhocSelected}
                      onChange={toggleAllAdhoc}
                      data-testid="adhoc-toggle-all"
                    />
                    <span className="text-body-sm font-medium text-amber-900">
                      {allAdhocSelected
                        ? t('recurring.preview.adhocDeselectAll', 'Deselect all')
                        : t('recurring.preview.adhocSelectAll', 'Select all')}
                    </span>
                  </label>
                </div>
              )}

              <RecurrencePreviewList
                occurrences={preview}
                onShift={shiftOccurrence}
                shiftingIndex={shiftingIndex}
                adhocSet={adhocSet}
                onToggleAdhoc={(dt) => {
                  setAdhocSet((prev) => {
                    const next = new Set(prev);
                    if (next.has(dt)) next.delete(dt);
                    else next.add(dt);
                    return next;
                  });
                }}
              />
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(4)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('newBooking.buttons.back', 'Back')}
            </Button>
            <Button
              onClick={() => createMutation.mutate(formData)}
              disabled={
                createMutation.isPending ||
                !preview ||
                totalToCreate === 0
              }
              size="lg"
              data-testid="create-series-button"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('recurring.actions.creating', 'Creating…')}
                </>
              ) : adhocSet.size > 0 ? (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  {t(
                    'recurring.actions.createWithAdhoc',
                    'Create {{total}} ({{adhoc}} with new availability)',
                    { total: totalToCreate, adhoc: adhocSet.size },
                  )}
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  {t('recurring.actions.skipAndCreate', 'Create {{count}} bookings', {
                    count: okCount,
                  })}
                </>
              )}
            </Button>
          </div>
        </StepCard>
      </Stepper>
        </div>

        <aside className="hidden lg:block">
          <BookingSummaryCard
            sticky
            title={t('recurring.summary.title', 'Recurring series')}
            rows={summaryRows}
            totalLabel={t('recurring.summary.total', 'Estimated total')}
            totalValue={totalValue}
            reassurance={null}
          />
        </aside>
      </div>
    </div>
  );
}

function StepNav({
  onPrev,
  onNext,
  disabledNext,
  t,
}: {
  onPrev?: () => void;
  onNext: () => void;
  disabledNext: boolean;
  t: any;
}) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      <Button
        type="button"
        variant="outline"
        onClick={onPrev}
        disabled={!onPrev}
        data-testid="back-button"
      >
        <ChevronLeft className="h-4 w-4 mr-2" />
        {t('newBooking.buttons.back', 'Back')}
      </Button>
      <Button type="button" onClick={onNext} disabled={disabledNext} data-testid="next-button">
        {t('newBooking.buttons.next', 'Next')}
        <ChevronRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

function FormField({
  label,
  required,
  icon,
  error,
  children,
}: {
  label: React.ReactNode;
  required?: boolean;
  icon?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {icon}
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {error && <p className="text-body-sm text-red-500">{error}</p>}
    </div>
  );
}
